// cartographer/fixupPrompt — turn a ValidationReport from Import JSON
// into a self-contained LLM prompt that asks the model to repair its
// own output. Phase 5 of the AI/JSON upgrade — closes the loop so users
// can paste the rejection list back into the same chat and converge in
// one round instead of debugging by hand.

import type { ValidationReport } from '@/lib/workflowValidation';

export function buildFixupPrompt(
  report: ValidationReport,
  originalJson: string,
): string {
  const errorLines: string[] = [];

  if (!report.jsonSyntaxOk) {
    errorLines.push(`JSON syntax: ${report.jsonSyntaxError ?? 'invalid'}`);
  }

  for (const section of report.sections) {
    if (section.ok || section.errors.length === 0) continue;
    errorLines.push(`\n[${section.label}]`);
    for (const err of section.errors.slice(0, 20)) {
      errorLines.push(`  - ${err}`);
    }
    if (section.errors.length > 20) {
      errorLines.push(`  … and ${section.errors.length - 20} more in this section`);
    }
  }

  const errorBlock = errorLines.length > 0
    ? errorLines.join('\n')
    : '(no specific errors — re-run the §8 self-check)';

  // Truncate the JSON payload to keep the prompt under ~12 KB. Most
  // LLMs handle 100+ KB but the user is pasting this into a chat box,
  // so keep it compact.
  const trimmedJson = originalJson.length > 8000
    ? originalJson.slice(0, 8000) + '\n…(truncated for prompt; see import dialog for full payload)'
    : originalJson;

  return `You produced the JSON below but it failed validation when I tried to
import it into EngiCoro Flow Weaver. Re-emit the JSON with these
rejections fixed — keep all the other content the same, do not start
over.

Pay particular attention to:
- Port-uniqueness per block-pair (each (block, port) anchors at most ONE connector).
- Lean-1 connectors must reference BLOCKS, not group ids.
- Cross-sheet connectors only between ADJACENT sheets (|Δ|=1).
- All x/y are multiples of 20 (block center, not top-left).
- Output is RAW JSON only — no markdown fences.

VALIDATION ERRORS:
${errorBlock}

ORIGINAL JSON (re-emit a fixed version):
\`\`\`json
${trimmedJson}
\`\`\``;
}
